/**
 * ConciliacionDelDia.jsx — la conciliación línea a línea contra el PSP, del día.
 *
 * QUÉ MUESTRA Y POR QUÉ NO ES `TesoreriaDelDia`
 * ----------------------------------------------
 * `TesoreriaDelDia` reparte la plata que YA se sabe que se cobró; esta pantalla
 * responde una pregunta anterior: ¿lo que dice el sistema coincide con lo que
 * dice el PSP y con lo que quedó escrito en la contabilidad? Son datos de un
 * endpoint distinto (`GET /api/conciliacion`, no `/api/dia`), así que este
 * componente hace SU PROPIA carga — no recibe `data` del padre como
 * `TesoreriaDelDia`, porque no es la misma plata la que está mirando.
 *
 * `payment` cruza tres fuentes (PSP · cargo · contabilidad) en dos ejes
 * independientes y devuelve un `Descuadre` por cada diferencia real, con su
 * motivo tipado (`src/services/conciliacion-psp.service.ts`, mismo repo que
 * `payment`). Acá sólo se pinta esa lista; NINGÚN motivo ni ninguna tolerancia
 * se recalcula en el cliente.
 *
 * LOS DOS AVISOS SON EL PUNTO CENTRAL DE LA PANTALLA. `resumen.eje_psp_omitido`
 * y `resumen.asiento_sin_cargo_omitido` dicen cuándo un eje de comparación NO
 * corrió — y una corrida a nivel de día SIEMPRE omite el segundo (el ledger
 * imputa `finance.movements.periodo` en formato mes, así que la query por día
 * nunca matchea). Sin este aviso, "0 descuadres" se lee como "todo cuadra"
 * cuando en realidad la mitad de la comparación nunca se hizo. Es exactamente
 * el error que este spec vino a prevenir.
 *
 * LAS DOS ACCIONES POR FILA
 * --------------------------
 * «Reconsultar» sólo tiene sentido en el eje `psp_vs_cargo`: es la única
 * comparación que una nueva consulta al PSP puede corregir (el eje
 * `cargo_vs_contabilidad` se arregla reprocesando el outbox, no volviendo a
 * preguntarle al proveedor). Además exige `subscription_charge_id`: el motivo
 * `sin_cargo` no tiene cuota local que reconsultar.
 *
 * «Marcar como resuelto» pide una nota OBLIGATORIA (spec §10.5,
 * `api/unmatched-resolver.ts` la exige igual del lado del servidor: sin nota no
 * hay POST). Se usa `window.prompt`, mismo patrón que la referencia de
 * transferencia del paso de giro en `YoolPayDia.jsx`.
 *
 * Las dos acciones, si funcionan, vuelven a pedir la conciliación completa: el
 * estado de esta pantalla nunca se edita a mano, siempre se refleja lo que
 * `payment` acaba de confirmar.
 *
 * UN HUECO CONOCIDO, YA NO ARREGLABLE DESDE ACÁ. `Descuadre` —el tipo que
 * `payment` devuelve en `GET /api/conciliacion`— no incluye el `id` de
 * `finance.unmatched_movements` (es un uuid que se asigna al insertar, y esta
 * pantalla lee el resultado de la función pura `conciliar()`, no una fila ya
 * guardada). `resolverDescuadre` sí exige ese `id`. Se manda `d.id` igual —por
 * si `payment` lo agrega en una corrección futura, mismo patrón que
 * `asiento_sin_cargo_omitido`, que se agregó después de este mismo plan— y si
 * no viene, el error que junta `payment` ("id y nota son obligatorios") se
 * muestra tal cual: no se inventa un id de reemplazo, porque ninguno de los
 * otros ids (`subscription_charge_id`, `external_charge_id`) es la llave
 * primaria de esa tabla, y usarlo podría resolver la fila equivocada si por
 * azar coincidiera con otra.
 *
 * @see src/services/yoolpay/conciliacionApi.js — el cliente de los cuatro endpoints
 * @see payment/src/services/conciliacion-psp.service.ts — de dónde sale cada campo
 * @see src/components/payments/yoolpay/TesoreriaDelDia.jsx — la plata ya conciliada
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import {
  getConciliacionDelDia,
  reconsultarCobro,
  resolverDescuadre,
} from '../../../services/yoolpay/conciliacionApi';
import {
  Aviso,
  Cifra,
  Encabezado,
  Fila,
  Pie,
  Rotulo,
  Seccion,
} from './cierre/piezas';
import { Badge, Boton } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp, fmtDelta, fmtNum } from '../../../lib/yoolpay/formato';

/** Frase corta por motivo — el catálogo cerrado de `chk_unmatched_reason`. */
const MOTIVO_LABEL = {
  sin_cargo: 'El PSP informa una transacción sin cargo local',
  no_informado_por_psp: 'Cuota pagada que el PSP no informó',
  monto_distinto_psp: 'Monto distinto entre el PSP y el cargo',
  fallido_pero_informado: 'Cargo fallido que el PSP dice haber procesado',
  sin_asiento: 'Cuota pagada sin asiento contable',
  asiento_sin_cargo: 'Asiento sin cargo en el universo conciliado',
  bruto_en_pago_propio: 'Bruto contable en un cargo de pago propio',
  monto_distinto_contabilidad: 'Monto distinto entre la contabilidad y el cargo',
};

/** `eje` → cómo se resuelve y con qué variante de `Badge`. */
const EJE = {
  psp_vs_cargo: { label: 'PSP vs cargo', variant: 'info' },
  cargo_vs_contabilidad: { label: 'Cargo vs contabilidad', variant: 'pendiente' },
};

const COLS = 'minmax(220px, 1.4fr) 150px minmax(140px, 1fr) 120px 230px';

/** La misma pieza «rótulo + cifra» que usa `TesoreriaDelDia`, sin exportarla desde ahí. */
function Metrica({ label, valor, color, hint }) {
  return (
    <div style={{ minWidth: 130 }}>
      <Rotulo>{label}</Rotulo>
      <div style={{ marginTop: 4 }}>
        <Cifra size={20} weight={700} color={color} align="left">{valor}</Cifra>
      </div>
      {hint ? (
        <p style={{ margin: '3px 0 0', fontSize: 11.5, color: 'var(--yool-muted)' }}>{hint}</p>
      ) : null}
    </div>
  );
}

// `psp` NUNCA lo pasa el caller actual (`YoolPayDia.jsx`) — a propósito, no un olvido.
// Omitirlo hace que `getConciliacionDelDia` concilie el período COMPLETO, incluidos los
// cargos sin `payment_provider` seteado (`payment`'s `conciliacion.worker.ts`: en julio
// 2026 eran 22 de 49 cuotas del mes). Si alguien lo cablea a un `psp_id` fijo para
// "completar" esta pantalla, esos cargos van a desaparecer de la conciliación sin aviso.
export default function ConciliacionDelDia({ fecha, psp }) {
  const { getToken } = useAuth();

  // Mismo criterio que `YoolPayDia`: `data === null` es «todavía no hay respuesta
  // buena» (cargando o falló), nunca un día sin descuadres. Un array vacío por
  // llamada fallida se leería como «este día cuadra perfecto».
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  /** Clave de la fila con una acción en curso (`'reconsultar:<id>'` / `'resolver:<id>'`). */
  const [enCurso, setEnCurso] = useState(null);
  const [accionError, setAccionError] = useState(null);

  const load = useCallback(async () => {
    if (!fecha) return;
    setCargando(true);
    setError(null);
    try {
      const token = await getToken();
      setData(await getConciliacionDelDia(token, fecha, psp));
    } catch (e) {
      setData(null);
      setError(e?.message ?? 'No se pudo cargar la conciliación');
    } finally {
      setCargando(false);
    }
  }, [getToken, fecha, psp]);

  useEffect(() => { load(); }, [load]);

  const onReconsultar = useCallback(async (d) => {
    if (!d.subscription_charge_id) return;
    const clave = `reconsultar:${d.subscription_charge_id}`;
    setAccionError(null);
    setEnCurso(clave);
    try {
      const token = await getToken();
      // payment deriva la ruta PSP (one_time vs subscription) de
      // subscriptions.orden_pago_id — un pago único también es una fila de
      // subscription_charges (ADR-017 §6), así que no hay que mandar `tipo`
      // acá: mandarlo fijo en 'subscription' era el bug (E-057 contra
      // pagos únicos, homologado a charge_not_found).
      const r = await reconsultarCobro(token, d.subscription_charge_id);
      // `encontrado:false` no es un error de la llamada — es el PSP diciendo que no
      // tiene nada nuevo. Sin este chequeo la fila queda exactamente igual y el único
      // rastro es que el botón dijo «Reconsultando…» por un instante: un no-op mudo.
      if (!r?.encontrado) {
        setAccionError('El PSP no devolvió datos para ese cargo — nada cambió.');
      }
      await load();
    } catch (e) {
      setAccionError(e?.message ?? 'No se pudo reconsultar el cobro');
    } finally {
      setEnCurso(null);
    }
  }, [getToken, load]);

  const onResolver = useCallback(async (d) => {
    // SIN `id` NO HAY FILA QUE CERRAR. `Descuadre` sale de `conciliar()`, una función
    // pura sobre datos en memoria — nunca tiene el uuid de `finance.unmatched_movements`
    // salvo que ya se haya POSTeado antes. Sin este guard, `JSON.stringify` descarta el
    // `id: undefined` y `payment` responde 400 "id y nota (no vacía) son obligatorios",
    // que se lee como si quien opera hubiera olvidado escribir la nota — no es eso: el
    // problema es que este descuadre nunca se guardó, así que no hay nada que resolver
    // todavía. Se corta ANTES del prompt para no pedir una nota que de todos modos no se
    // va a poder mandar.
    if (!d.id) {
      setAccionError(
        'Este descuadre se calculó en vivo y todavía no está en la bitácora — no se '
        + 'puede cerrar desde esta pantalla.',
      );
      return;
    }

    // OBLIGATORIA: un string vacío o sólo espacios aborta sin llamar al
    // servicio — `api/unmatched-resolver.ts` la exige igual del lado del
    // servidor, y duplicar el chequeo evita un viaje de red que iba a volver
    // con el mismo 400.
    const nota = window.prompt(
      `Nota de cierre para «${MOTIVO_LABEL[d.motivo] ?? d.motivo}» (obligatoria):`,
      '',
    );
    if (nota == null || nota.trim() === '') return;

    const clave = `resolver:${d.id ?? d.subscription_charge_id ?? d.external_charge_id}`;
    setAccionError(null);
    setEnCurso(clave);
    try {
      const token = await getToken();
      await resolverDescuadre(token, d.id, nota.trim());
      await load();
    } catch (e) {
      setAccionError(e?.message ?? 'No se pudo marcar el descuadre como resuelto');
    } finally {
      setEnCurso(null);
    }
  }, [getToken, load]);

  if (!fecha) return null;

  const resumen = data?.resumen ?? null;
  const descuadres = data?.descuadres ?? [];
  const porMotivo = data?.por_motivo ?? [];

  return (
    <Seccion
      titulo="Conciliación PSP del día"
      bajada={
        'Cruce línea a línea de lo que el PSP informa, lo que el sistema cobró y lo que '
        + 'quedó en la contabilidad. Cada fila es una diferencia real — los redondeos de '
        + 'menos de un peso no se listan porque no hay nada que corregir en ellos.'
      }
    >
      {error ? (
        <div style={{ padding: 20 }}>
          <Aviso tono="alta" titulo="No se pudo cargar la conciliación">
            {error} — no se muestra ningún descuadre porque la llamada falló, no porque el
            día cuadre.
          </Aviso>
        </div>
      ) : null}

      {accionError ? (
        <div style={{ padding: '0 24px 16px' }}>
          <Aviso tono="alta" titulo="La acción no se ejecutó">
            {accionError}
          </Aviso>
        </div>
      ) : null}

      {!error && !data ? (
        <p style={{ padding: 24, margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
          {cargando ? 'Conciliando el día…' : 'Sin datos todavía.'}
        </p>
      ) : null}

      {!error && data ? (
        <div style={{ display: 'grid', gap: 16, padding: '18px 24px' }}>

          {/* LOS DOS AVISOS DE OMISIÓN, arriba de todo y siempre visibles cuando
              aplican — no dentro de un acordeón que alguien puede no abrir. Son la
              razón de ser de esta pantalla: sin ellos, la lista de abajo (aunque
              esté vacía) se lee como una garantía que no es. */}
          {resumen?.eje_psp_omitido ? (
            <Aviso tono="alta" titulo="El eje PSP-vs-cargo se omitió por completo.">
              No llegó ningún reporte de liquidación del PSP para este día (ni el espejo de
              <code> payment_metadata</code>), así que ninguna cuota se comparó contra el
              proveedor. Los descuadres de abajo son sólo del eje contable — «0 descuadres
              con el PSP» acá NO significa que el PSP haya confirmado nada.
            </Aviso>
          ) : null}

          {resumen?.asiento_sin_cargo_omitido ? (
            <Aviso tono="media" titulo="El motivo «asiento sin cargo» no se pudo revisar hoy.">
              Esta corrida es de un día, y <code>finance.movements.periodo</code> se imputa en
              formato mes: un asiento huérfano de otra fecha del mismo mes no aparece acá. Esa
              comparación sólo es completa a nivel de mes.
            </Aviso>
          ) : null}

          {data.descuadres_truncados ? (
            <Aviso tono="media" titulo="La lista se truncó.">
              Hay más descuadres reales que los que se muestran; el resumen numérico de abajo
              sí los cuenta a todos.
            </Aviso>
          ) : null}

          {/* El resumen numérico de la corrida, no recalculado: son las mismas
              cifras que `payment` ya agregó en `resumen`. */}
          {resumen ? (
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <Metrica label="Líneas conciliadas" valor={fmtNum(resumen.lineas_totales)} />
              <Metrica
                label="Cuadraron"
                valor={fmtNum(resumen.lineas_apareadas)}
                color={CIFRA.verde}
                hint={resumen.lineas_redondeo > 0
                  ? `${fmtNum(resumen.lineas_redondeo)} con redondeo de menos de $1`
                  : undefined}
              />
              <Metrica
                label="Con descuadre real"
                valor={fmtNum(resumen.lineas_con_descuadre)}
                color={resumen.lineas_con_descuadre > 0 ? CIFRA.rojo : CIFRA.apagado}
              />
              <Metrica
                label="Plata a buscar"
                valor={fmtClp(resumen.descuadre_real_clp)}
                color={resumen.descuadre_real_clp > 0 ? CIFRA.rojo : CIFRA.apagado}
                hint="Suma de |delta| de los descuadres reales"
              />
            </div>
          ) : null}

          {/* La lista de descuadres. */}
          {descuadres.length === 0 ? (
            <Aviso tono={resumen?.eje_psp_omitido ? 'media' : 'ok'} titulo="Sin descuadres reales.">
              {resumen?.eje_psp_omitido
                ? 'Ninguno en el eje contable — el eje con el PSP no corrió, ver el aviso de arriba.'
                : 'Ninguna de las dos comparaciones dejó una diferencia por sobre el redondeo.'}
            </Aviso>
          ) : (
            <div style={{ border: '1px solid var(--yool-border)', borderRadius: 12, overflow: 'hidden' }}>
              <Encabezado cols={COLS}>
                <Rotulo>Motivo</Rotulo>
                <Rotulo>Eje</Rotulo>
                <Rotulo>PSP / cargo</Rotulo>
                <Rotulo align="right">Delta</Rotulo>
                <Rotulo align="right">Acciones</Rotulo>
              </Encabezado>

              {descuadres.map((d) => {
                const eje = EJE[d.eje] ?? { label: d.eje, variant: 'neutro' };
                const identidad = d.external_charge_id ?? d.subscription_charge_id ?? '—';
                const puedeReconsultar = d.eje === 'psp_vs_cargo' && !!d.subscription_charge_id;
                const claveFila = d.id
                  ?? `${d.motivo}:${d.subscription_charge_id ?? d.external_charge_id ?? d.movement_id}`;
                const reconsultando = enCurso === `reconsultar:${d.subscription_charge_id}`;
                const resolviendo =
                  enCurso === `resolver:${d.id ?? d.subscription_charge_id ?? d.external_charge_id}`;
                return (
                  <Fila cols={COLS} key={claveFila} alinear="start">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {MOTIVO_LABEL[d.motivo] ?? d.motivo}
                      </div>
                      {d.detalle ? (
                        <div style={{ fontSize: 11.5, color: 'var(--yool-muted)', marginTop: 3 }}>
                          {d.detalle}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <Badge variant={eje.variant}>{eje.label}</Badge>
                    </div>
                    <div style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {identidad}
                    </div>
                    {/* Toda fila de esta lista es, por definición de la sección, un
                        descuadre real: el signo de `delta` sólo dice quién reportó más,
                        no cuán grave es. Rojo es «no cuadra» (estados.js) y CADA fila no
                        cuadra — ámbar («hay que mirarlo») subestimaría justo los motivos
                        siempre-negativos (`no_informado_por_psp`, `sin_asiento`: plata
                        que se cobró y el PSP/el ledger nunca vieron), que son los más
                        graves de los dos ejes. Mismo criterio que `Metrica` más abajo. */}
                    <Cifra color={d.delta !== 0 ? CIFRA.rojo : CIFRA.apagado}>
                      {fmtDelta(d.delta)}
                    </Cifra>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      {puedeReconsultar ? (
                        <Boton
                          variant="outline"
                          onClick={() => onReconsultar(d)}
                          disabled={reconsultando || resolviendo}
                        >
                          {reconsultando ? 'Reconsultando…' : 'Reconsultar'}
                        </Boton>
                      ) : null}
                      <Boton
                        variant="outline"
                        onClick={() => onResolver(d)}
                        disabled={reconsultando || resolviendo}
                      >
                        {resolviendo ? 'Resolviendo…' : 'Marcar como resuelto'}
                      </Boton>
                    </div>
                  </Fila>
                );
              })}

              <Pie total={fmtNum(descuadres.length)} totalLabel="Descuadres">
                {porMotivo.length > 0
                  ? porMotivo
                    .map((g) => `${MOTIVO_LABEL[g.motivo] ?? g.motivo} (${fmtNum(g.cantidad)})`)
                    .join(' · ')
                  : 'Sin agrupación por motivo.'}
              </Pie>
            </div>
          )}
        </div>
      ) : null}
    </Seccion>
  );
}
